import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Finding } from "@/app/pages/auditcentre";
import { Textarea } from '../ui/textarea';
import { SingleUserCombobox } from '../ui/SingleUserCombobox';
import { useTenancy } from '@/contexts/TenancyContext';
import { AuditRegister } from '@/js/external-js/variableModules/namespaces/AuditRegister.namespace';
import { EntityLinkedDocumentsSection } from './EntityLinkedDocumentsSection';

type FindingEditDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  finding: Finding | null;
  onSave: (finding: Omit<Finding, 'id' | 'correctiveActions'>) => void;
  isSubmitting?: boolean;
  // DED-973: Controls to inherit from parent audit
  inheritedControls?: string[];
};

export function FindingEditDialog({ 
  open, 
  onOpenChange, 
  finding, 
  onSave, 
  isSubmitting = false,
  inheritedControls = []
}: FindingEditDialogProps) {
  const { activeTenancy } = useTenancy();
  const [auditSettings, setAuditSettings] = useState<{ findingTypes: string[]; riskCategories: string[] }>({
    findingTypes: [],
    riskCategories: []
  });
  const [auditLoading, setAuditLoading] = useState(true);
  const [formData, setFormData] = React.useState<Omit<Finding, 'id' | 'correctiveActions'>>({
    type: '',
    status: 'Open',
    summary: '',
    owner: '',
    auditId: finding?.auditId,
    origin: finding?.origin || 'standalone',
    createdAt: finding?.createdAt || new Date(),
    updatedAt: new Date(),
    comments: finding?.comments || '',
    dateClosed: finding?.dateClosed,
    // DED-973: Initialize linkedDocuments
    linkedDocuments: finding?.linkedDocuments || []
  });

  // Load audit settings when dialog opens
  React.useEffect(() => {
    if (open && activeTenancy?.id) {
      setAuditLoading(true);
      AuditRegister.Audit.Menu(activeTenancy.id)
        .then(menuData => {
          if (menuData) {
            setAuditSettings({
              findingTypes: menuData.findingTypes || [],
              riskCategories: menuData.riskCategories || []
            });
            // Set default type if form data type is empty
            if (!formData.type && menuData.findingTypes?.length > 0) {
              setFormData(prev => ({ ...prev, type: menuData.findingTypes[0] }));
            }
          }
        })
        .catch(error => {
          console.error('Error loading audit settings:', error);
        })
        .finally(() => {
          setAuditLoading(false);
        });
    }
  }, [open, activeTenancy?.id, formData.type]);

  React.useEffect(() => {
    if (finding) {
      setFormData({
        type: finding.type,
        status: finding.status,
        summary: finding.summary,
        owner: finding.owner,
        auditId: finding.auditId,
        origin: finding.origin,
        createdAt: finding.createdAt || new Date(),
        comments: finding.comments || '',
        updatedAt: new Date(),
        dateClosed: finding.dateClosed,
        // DED-973: Load linkedDocuments
        linkedDocuments: finding.linkedDocuments || []
      });
    } else {
      setFormData({
        type: 'OFI',
        status: 'Open',
        summary: '',
        owner: '',
        auditId: finding?.auditId,
        origin: 'standalone',
        createdAt: new Date(),
        comments: '',
        updatedAt: new Date(),
        dateClosed: undefined,
        // DED-973: Reset linkedDocuments
        linkedDocuments: []
      });
    }
  }, [finding]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSave(formData);
      onOpenChange(false); // Close the dialog after successful save
    } catch (error) {
      console.error('Failed to save finding:', error);
      // Error handling can be added here if needed
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">Edit Finding</DialogTitle>
          </div>
        <form id="finding-edit-form" onSubmit={handleSubmit} className="space-y-4 max-w-full overflow-hidden px-4 pb-4">
          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData({...formData, type: value as any})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {/* Show current type even if it's not in the configurable list */}
                {formData.type && !auditSettings.findingTypes?.includes(formData.type) && (
                  <SelectItem value={formData.type}>
                    {formData.type} (Legacy)
                  </SelectItem>
                )}
                {auditSettings.findingTypes?.map((findingType: string) => (
                  <SelectItem key={findingType} value={findingType}>
                    {findingType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => {
                const newStatus = value as 'Open' | 'Closed';
                setFormData({
                  ...formData, 
                  status: newStatus,
                  // DED-1141: Auto-set dateClosed when status changes to Closed
                  dateClosed: newStatus === 'Closed' ? (formData.dateClosed || new Date().toISOString().split('T')[0]) : undefined
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* DED-1141: Show Date Closed field when status is Closed */}
          {formData.status === 'Closed' && (
            <div className="space-y-2">
              <Label htmlFor="dateClosed">Date Closed</Label>
              <Input
                id="dateClosed"
                type="date"
                value={formData.dateClosed ? (typeof formData.dateClosed === 'string' ? formData.dateClosed.split('T')[0] : new Date(formData.dateClosed).toISOString().split('T')[0]) : ''}
                onChange={(e) => setFormData({...formData, dateClosed: e.target.value})}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="summary">Summary</Label>
            <Textarea
              id="summary"
              value={formData.summary}
              onChange={(e) => setFormData({...formData, summary: e.target.value})}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="owner">Owner</Label>
            <div className="w-full max-w-full overflow-hidden">
              <div className="w-full max-w-full overflow-hidden">
                <SingleUserCombobox
                  selected={formData.owner}
                  setSelected={(value) => setFormData({...formData, owner: value})}
                  placeholder="Select owner"
                  className="max-w-full"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="comments">Comments</Label>
            <Textarea
              id="comments"
              value={formData.comments}
              onChange={(e) => setFormData({...formData, comments: e.target.value})}
            />
          </div>

          {/* DED-973: Linked Documents Section */}
          <div className="mt-4 pt-4 border-t">
            <EntityLinkedDocumentsSection
              entityType="Finding"
              linkedDocuments={formData.linkedDocuments || []}
              onDocumentsChange={(docs) => setFormData({...formData, linkedDocuments: docs})}
              inheritedControls={inheritedControls}
              entityTitle={formData.summary || 'Finding'}
            />
          </div>

        </form>
        <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" form="finding-edit-form" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
